import { InstrumentFamilyTitles } from 'constants/api/instruments';
import { freeDescriptorMessage } from 'constants/free';
import React, { useMemo, useCallback, useState, useRef, memo } from 'react';
import styled from 'styled-components';
import {
    Badge,
    Group,
    FeatureCard,
    PackOutlined,
    ProviderOutlined,
    useOnClickOutside,
    Button,
    Text,
} from '@landr/maestro';
import { Link } from 'react-router-dom';
import { Trans, t } from '@lingui/macro';
import { analytics } from 'utils/analytics';
import { getIconWithTooltipBySampleType, getImageUrl } from 'helpers';
import { Sample, CommonCardProps, InstrumentationOptions } from 'types';
import { SampleType, RoutePaths } from 'types/enums';
import { useTrackingContext } from 'contexts/TrackingContext/TrackingContext';
import SampleWaveformPlayer from 'components/SampleWaveformPlayer';
import PlayerButton from 'components/CommonUi/PlayerButton/PlayerButton';
import { DownloadButton } from 'components/CommonUi/DownloadButton';
import { SelectorButton } from 'components/CommonUi/SelectorButton/SelectorButton';
import { useGenericCarouselContext } from 'components/GenericCarousel';
import AddSampleToCollectionButton from 'components/CommonUi/AddSampleToCollectionButton';
import { CreatorButton } from 'components/CommonUi/CreatorButton/CreatorButton';
import { FavoriteButton } from 'components/FavoriteButton';
import { usePartner } from 'hooks/usePartner';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';

export interface SampleCardProps extends CommonCardProps<Sample> {
    imageId?: string;
}

interface DetailButtonProps {
    carouselName: string;
    carouselId: string;
    sample: Sample;
    partner?: { route: string; slug?: string };
}

// TODO: extract and reuse in `ContextMenu` in samples table column
const DetailButton: React.FC<DetailButtonProps> = ({ partner, sample }) => {
    const hasPartner = !!partner;
    const toLocalizedPath = useToLocalizedPath();
    const toPartner = toLocalizedPath(`/${partner?.route}/${partner?.slug}`);
    const toPack = toLocalizedPath(`/${RoutePaths.Packs}/${sample.pack.slug}`);

    const to = hasPartner ? toPartner : toPack;

    return (
        <Button
            as={Link}
            to={to}
            kind="link"
            variant="secondary"
            icon={hasPartner ? ProviderOutlined : PackOutlined}
            onClick={(): void => {
                analytics.trackCTAClicked({
                    ctaGoal: CtaGoal.ViewDetail,
                    ctaType: CtaType.Dropdown,
                    interactionSource: InteractionSource.AssetActionDropdown,
                    ctaDestination: to,
                    ctaText: `View ${hasPartner ? `partner` : `pack`}`,
                });
            }}
        >
            <Trans>View {hasPartner ? `partner` : `pack`}</Trans>
        </Button>
    );
};

export const Center = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
`;

const SampleCardWrapper = styled(Group)<any>`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const getBadges = (sample: Sample): JSX.Element[] => {
    const badges = [];

    // Sample type
    const { Icon } = getIconWithTooltipBySampleType(sample?.sampleType);

    if (Icon) {
        badges.push(<Badge icon={Icon} kind="subtle" />);
    }

    // Instrument
    if (sample.instrumentFamily) {
        badges.push(
            <Badge
                content={InstrumentFamilyTitles[sample.instrumentFamily.code]}
                kind="subtle"
            />,
        );
    }

    // Free
    const isFree = sample?.credits === 0;

    if (isFree) {
        badges.push(<Badge content={freeDescriptorMessage} kind="subtle" />);
    }

    return badges;
};

export const SampleCard: React.FC<SampleCardProps> = memo(
    ({ item, imageId, isSelected, setSelected, autoplay = true, ...props }) => {
        const { carouselName, carouselId } = useGenericCarouselContext();
        const { trackingDispatch } = useTrackingContext();
        const partner = usePartner(item?.pack);
        const [subActions, setSubActions] = useState<any>(null);

        const subActionsContainerRef = useRef<HTMLDivElement>(null);

        const handleClickOutside = (): void => {
            setTimeout(() => {
                setSubActions(null);
            }, 500);
        };

        useOnClickOutside(subActionsContainerRef, handleClickOutside);

        const handleSampleClickPlay = useCallback(() => {
            trackingDispatch({
                type: 'samplePlayed',
                payload: {
                    item,
                    source: {
                        interaction: `Sample Card`,
                        carouselId: carouselId,
                        carouselName: carouselName,
                    },
                },
            });
        }, [carouselId, carouselName, trackingDispatch, item]);

        const instrumentationOptions: InstrumentationOptions = {
            interactionSource: 'Sample Card',
            ...(carouselName && { carouselName: carouselName }),
            ...(carouselId && { carouselId: carouselId }),
        };

        const renderActions = (sample: Sample): JSX.Element[] => {
            const actions = [
                <PlayerButton
                    size="sm"
                    variant="subtle"
                    isRounded
                    id={sample.guid}
                    callback={handleSampleClickPlay}
                />,
                <SelectorButton
                    size="sm"
                    variant="subtle"
                    isRounded
                    sample={sample}
                    instrumentationOptions={instrumentationOptions}
                />,
                <FavoriteButton
                    key={`reconciliation-isFavorited-${sample.isFavorited}`}
                    size="sm"
                    variant="subtle"
                    isRounded
                    item={sample}
                />,
                <DownloadButton
                    size="sm"
                    variant="subtle"
                    isRounded
                    item={sample}
                />,
            ];

            if (sample?.sampleType === SampleType.Loop) {
                actions.push(
                    <CreatorButton
                        size="sm"
                        variant="subtle"
                        isRounded
                        sample={sample}
                    />,
                );
            }

            return actions;
        };

        const renderSubActions = useMemo(() => {
            return subActions
                ? React.cloneElement(subActions, {
                      ref: subActionsContainerRef,
                      key: item.guid,
                  })
                : [
                      <AddSampleToCollectionButton
                          samples={[item]}
                          source={{
                              interaction: 'Sample Card',
                              carouselName: carouselName,
                              carouselId: carouselId,
                          }}
                          kind="link"
                          variant="secondary"
                          setPopover={setSubActions}
                          hide={(): void => setSubActions(null)}
                      >
                          <Trans>Add to collection</Trans>
                      </AddSampleToCollectionButton>,

                      <DetailButton
                          carouselName={carouselName}
                          carouselId={carouselId}
                          sample={item}
                      ></DetailButton>,
                      <>
                          {partner && (
                              <DetailButton
                                  carouselName={carouselName}
                                  carouselId={carouselId}
                                  sample={item}
                                  partner={partner}
                              ></DetailButton>
                          )}
                      </>,
                  ];
        }, [subActions, partner, item, carouselName, carouselId]);

        const isFree = item?.credits === 0;

        const badges = useMemo(() => getBadges(item), [item]);

        return (
            <SampleCardWrapper
                direction="vertical"
                alignItems="flex-start"
                gutter="xs"
                {...props}
            >
                <FeatureCard
                    data-testid="feature-card"
                    data-isdownloaded={item.isDownloaded}
                    data-isfree={isFree}
                    as={Group.Item}
                    badges={badges}
                    media={
                        item?.pack?.artworkUrl && (
                            <FeatureCard.Media
                                src={getImageUrl(item?.pack?.artworkUrl, 30)}
                                fallbackSrc={
                                    '/assets/fallback/artworkPlaceholder.svg'
                                }
                                alt={t`Cover of ${item?.pack?.name}`}
                            />
                        )
                    }
                    backgroundColor={item?.pack?.backgroundColor}
                    actions={renderActions(item)}
                    subActions={renderSubActions}
                    isSelected={isSelected}
                >
                    <Center>
                        <SampleWaveformPlayer
                            sample={item}
                            theme={item?.pack?.foregroundColor}
                        />
                    </Center>
                </FeatureCard>
                <Group.Item>
                    <Text size="sm" fontWeight="bold">
                        {item.name}
                    </Text>
                </Group.Item>
            </SampleCardWrapper>
        );
    },
);

export default SampleCard;
